import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { snodeApi, ansApi } from "../api";
import { baseAPIBody } from "../api";
import "./TransactionPage.css";
import { formatAmount, basePath, truncateWalletAddress, isOnlyZero } from "../utils";

const TransactionPage = () => {
  const { hash } = useParams();
  const [txData, setTxData] = useState({
    txHash: "",
    blockHeight: "",
    blockHash: "",
    timestamp: "",
    type: "",
    from: [],
    to: [],
    fee: "",
  });
  const [isMempool, setIsMempool] = useState(false);
  const [loading, setLoading] = useState(true);
  const [latestBlockHeight, setLatestBlockHeight] = useState(0);
  const [activeTab, setActiveTab] = useState("details");
  const [rawData, setRawData] = useState("");
  const [formatedAddresses, setFormatedAddresses] = useState({
    from: [],
    to: [],
  });
  const [trigger, setTrigger] = useState(false);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(hash);
  const [error, setError] = useState("");
  const baseUrl = process.env.REACT_APP_ABELIAN_NAME_SERVICE_API_BASE_URL;

  const getAbelianAddress = async (fingerprint) => {
    if (!fingerprint) {
      return "-";
    }
    if (isOnlyZero(fingerprint)) {
      return fingerprint;
    }
    return ansApi.get(
      `/query/${fingerprint}?include_abel_address=true`,
      baseUrl
    ).catch((err) => {
      // abe…[FINGERPRINT]…
      return { data: { payload: { short_address: `abe…${fingerprint}…` } } };
    });
  };

  const getAllAddresses = async (fingerprints) => {
    const responses = await Promise.all(fingerprints.map(getAbelianAddress));
    return responses.map((res) => res.data?.payload?.short_address || "-");
  };

  useEffect(() => {
    const getAddresses = async () => {
      try {
        const fingerprints = [...txData.from, ...txData.to];
        const addresses = await getAllAddresses(fingerprints);
        if (addresses.length > 0) {
          const from = addresses.slice(0, txData.from.length);
          const to = addresses.slice(txData.from.length);
          setFormatedAddresses({
            from: from,
            to: to,
          });
        }
      } catch (error) {
        console.log(
          "get addresses error",
          error?.response?.data?.error?.message
        );
      }
    };
    getAddresses();
  }, [txData]);

  const searchMempool = useCallback(async () => {
    try {
      const response = await snodeApi.post(`/${basePath}`, {
        params: {
          txid: `${hash}`,
        },
        method: "abelsn_chainMempool",
        ...baseAPIBody,
      });
      if (response.data.result) {
        const result = response.data.result;
        const tx = result.transactions.find((el) => el.txid === hash);
        if (tx) {
          setTxData({
            blockHeight: tx.blockHeight,
            blockHash: tx.blockHash,
            timestamp: tx.created_at,
            type: tx.type,
            from: tx.input.flatMap((item) =>
              item.prevutoxring.outpoints.map((outpoint) => {
                if (Object.keys(outpoint).includes("fingerprint")) {
                  return outpoint.fingerprint;
                }
                return undefined;
              })
            ),
            to: tx.output.map((item) => item.fingerprint),
            fee: tx.fee,
          });
          setRawData(result);
          setIsMempool(true);
        } else {
          setError("Transaction not found ");
        }
      }
    } catch (error) {
      setError(error?.response?.data?.error?.message || "search mempool error");
    }
  }, [hash]);

  useEffect(() => {
    setError("");
    setIsMempool(false);
  }, [txData.txHash]);

  useEffect(() => {
    setTxData({
      txHash: "",
      blockHeight: "",
      blockHash: "",
      timestamp: "",
      type: "",
      from: [],
      to: [],
      fee: "",
    });
  }, [hash]);

  useEffect(() => {
    const fetchTxData = async () => {
      setLoading(true);
      try {
        const response = await snodeApi.post(`/${basePath}`, {
          params: {
            txid: `${hash}`,
          },
          method: "abelsn_chainTransactionView",
          ...baseAPIBody,
        });
        if (response.data.result) {
          const result = response.data.result;
          const _data = {
            txHash: result.txid,
            blockHeight: result.blockHeight,
            blockHash: result.blockHash,
            timestamp: result.timestamp,
            type: result.type,
            from: result.input.flatMap((item) =>
              item.prevutoxring.outpoints.map(
                (outpoint) => outpoint.fingerprint
              )
            ),
            to: result.output.map((item) => item.fingerprint),
            fee: formatAmount(result.fee),
          };
          setTxData(_data);
          setRawData(result);
        } else {
          console.log("no result, searching mempool");
          await searchMempool();
        }
      } finally {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    };

    fetchTxData();
  }, [hash, searchMempool, trigger]);

  // get latest block height
  useEffect(() => {
    const fetchChainStatus = async () => {
      const response = await snodeApi.post(`/${basePath}`, {
        params: {},
        method: "abelsn_chainStatus",
        ...baseAPIBody,
      });
      if (response.data && response.data.result) {
        setLatestBlockHeight(response.data.result.latestBlockHeight);
      }
    };

    fetchChainStatus();
  }, [hash]);

  if (loading) return <div className="loading">Loading...</div>;
  if (!txData) return <div>Transaction not found</div>;
  if (error) return <div>{error}</div>;
  return (
    <div className="tx-page">
      <div className="search-bar">
        <input
          type="text"
          placeholder="Search by TX Hash"
          onChange={(e) => {
            setSearchValue(e.target.value?.trim());
          }}
          value={searchValue}
        />
        <button
          onClick={() => {
            navigate(`/tx/${searchValue}`);
            setTrigger(!trigger);
          }}
        >
          🔍
        </button>
      </div>
      <h2>Transaction Details</h2>
      <div className="tabs">
        <button
          className={`tab ${activeTab === "details" ? "active" : ""}`}
          onClick={() => setActiveTab("details")}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === "rawdata" ? "active" : ""}`}
          onClick={() => setActiveTab("rawdata")}
        >
          Raw Data
        </button>
      </div>
      {activeTab === "details" && (
        <div className="tx-details">
          {isMempool && <span className="mempool-tag">Mempool</span>}
          <div className="detail-item">
            <span className="label">Transaction Hash:</span>
            <span className="value">
              {truncateWalletAddress(txData.txHash)}
            </span>
          </div>
          <div className="detail-item">
            <span className="label">Status:</span>
            <span className="value success">
              {latestBlockHeight - txData.blockHeight < 4
                ? "Unconfirmed"
                : "Confirmed"}
            </span>
          </div>
          <div className="detail-item">
            <span className="label">Block Height:</span>
            <span className="value">
              {txData.blockHeight}
              &nbsp; ({latestBlockHeight - txData.blockHeight} confirmations)
            </span>
          </div>
          <div className="detail-item">
            <span className="label">Block Hash:</span>
            <span className="value">
              {truncateWalletAddress(txData.blockHash)}
            </span>
          </div>
          <div className="detail-item">
            <span className="label">Timestamp:</span>
            <span className="value">
              {new Date(txData.timestamp).toLocaleString()}
            </span>
          </div>
          <div className="detail-item">
            <span className="label">Type:</span>
            <span className="value">{txData.type}</span>
          </div>
          <div className="detail-item">
            <span className="label">From:</span>
            <div className="value">
              {formatedAddresses.from.map((item, index) => (
                <div key={index}>
                  <span title={item}>{truncateWalletAddress(item)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="detail-item">
            <span className="label">To:</span>
            <div className="value">
              {formatedAddresses.to.map((item, index) => (
                <div key={index}>
                  <span title={item}>{truncateWalletAddress(item)}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="detail-item">
            <span className="label">Fee:</span>
            <span className="value">{txData.fee} ABEL</span>
          </div>
        </div>
      )}
      {activeTab === "rawdata" && (
        <div className="tx-details tx-rawdata">
          <code>{JSON.stringify(rawData, null, 2)}</code>
        </div>
      )}
    </div>
  );
};

export default TransactionPage;
